import {useQuery} from '@apollo/client';
import {useEffect, useState, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {GET_POD_REVIEW_REVIEWERS, GetPodReviewReviewersResponse} from '../graphql/queries/getPodReviewReviewers';
import {getUserInfo, isReportDownloadSelfOnlyRole} from '../../../../utils/auth';
import {ReportFileFiltersType} from './useCsReportFileQuery';
import {getLastWeekDateTimeRange} from '../../../../utils/dateTimeUtils';

interface UseReportOperatorsResult {
    operatorOptions: {value: string, label: string}[];
    loading: boolean;
    defaultFilters: ReportFileFiltersType | null;
}

export const useReportOperators = (): UseReportOperatorsResult => {
    const {t, i18n} = useTranslation();
    const [operatorOptions, setOperatorOptions] = useState<{value: string, label: string}[]>([]);
    const [defaultFilters, setDefaultFilters] = useState<ReportFileFiltersType | null>(null);

    // 获取"全部"的翻译文本，确保翻译已加载
    const allLabel = useMemo(() => {
        const translated = t('central.download.filters.all');
        // 如果返回的是 key 本身，说明翻译未找到，使用默认值
        return translated === 'central.download.filters.all' ? (i18n.language === 'zh' ? '全部' : 'All') : translated;
    }, [t, i18n.language]);

    const selfOnly = isReportDownloadSelfOnlyRole();

    // 仅本人可见时下拉锁定为自己，无需拉审核人列表（该列表只含 KAQC 角色，不含客服账号）
    const { data: reviewersData, loading } = useQuery<GetPodReviewReviewersResponse>(GET_POD_REVIEW_REVIEWERS, {
        skip: selfOnly
    });

    // 处理审核人数据变化并设置默认过滤条件
    useEffect(() => {
        const currentUser = getUserInfo();
        if (!selfOnly && !reviewersData?.getPodReviewReviewers) {
            return;
        }

        // 设置默认日期为最近一周
        const [startDate, endDate] = getLastWeekDateTimeRange();
        let filters: ReportFileFiltersType = {
            type: undefined,
            name: undefined,
            reportDate: [startDate, endDate],
            operatorId: undefined,
        };

        if (selfOnly) {
            setOperatorOptions(
                currentUser ? [{ value: currentUser.account.id, label: currentUser.account.name }] : []
            );
            if (currentUser) {
                filters.operatorId = parseInt(currentUser.account.id);
            }
            setDefaultFilters(filters);
            return;
        }

        const reviewers = reviewersData!.getPodReviewReviewers;
        setOperatorOptions([
            { value: '', label: allLabel },
            ...reviewers.map(reviewer => ({ value: reviewer.id, label: `${reviewer.name}` }))
        ]);

        if (currentUser) {
            const currentUserId = parseInt(currentUser.account.id);
            const currentReviewer = reviewers.find(reviewer => parseInt(reviewer.id) === currentUserId);
            if (currentReviewer) {
                filters.operatorId = currentUserId;
            }
        }

        setDefaultFilters(filters);
    }, [reviewersData, allLabel, selfOnly]);

    return {
        operatorOptions,
        loading,
        defaultFilters
    };
};
